import React, { useState, useRef } from 'react';
import { Menu, X, Mail, MessageSquare, MapPin } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { services, manufacturingProcess, industries, whyChooseUs, stats, heroVideo, heroVideoPoster } from '../data/mock';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import CookieConsent from './CookieConsent';
import Modal from './Modal';
import { PrivacyPolicyContent, CookiePolicyContent, TermsOfServiceContent } from './PolicyContent';
import { useCountAnimation, useInView } from '../hooks/useAnimations';

// Animated stat counter component
const AnimatedStat = ({ stat }) => {
  const [ref, isInView] = useInView({ threshold: 0.3 });
  const count = useCountAnimation(stat.numericValue, 2000, isInView);
  
  return (
    <div 
      ref={ref}
      className='bg-white/10 backdrop-blur-md rounded-lg p-6 border border-white/20 transform transition-all duration-500 hover:scale-105'
    >
      <div className='text-3xl md:text-4xl font-bold mb-2'>
        {isInView ? `${count}${stat.suffix}` : stat.value}
      </div>
      <div className='text-sm md:text-base'>{stat.label}</div>
    </div>
  );
};

// Animated section wrapper for fade-in effects
const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const [ref, isInView] = useInView({ threshold: 0.1 });
  
  return (
    <div 
      ref={ref}
      className={`transform transition-all duration-700 ${className}`}
      style={{ 
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(30px)',
        transitionDelay: `${delay}ms`
      }}
    >
      {children}
    </div>
  );
};

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [cookieModalOpen, setCookieModalOpen] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const videoRef = useRef(null);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  const getIcon = (iconName) => {
    const Icon = LucideIcons[iconName];
    return Icon ? <Icon className='w-6 h-6' /> : null;
  };

  return (
    <div className='min-h-screen'>
      {/* Fixed Hero Background - Video with smooth poster transition */}
      <div className='fixed top-0 left-0 right-0 bottom-0 z-0 overflow-hidden'>
        {/* Poster image - visible until video is ready */}
        <div 
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${videoReady ? 'opacity-0' : 'opacity-100'}`}
          style={{
            backgroundImage: `url(${heroVideoPoster})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          }}
        />
        
        {/* Video Background - fades in smoothly once ready */}
        <video
          ref={videoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          onCanPlayThrough={() => setVideoReady(true)}
          onPlaying={() => setVideoReady(true)}
          className={`absolute w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${videoReady ? 'opacity-100' : 'opacity-0'}`}
        >
          <source src={heroVideo} type="video/mp4" />
        </video>
        
        {/* Gradient Overlay - always visible for consistent look */}
        <div className='absolute inset-0 bg-gradient-to-br from-blue-900/80 via-blue-800/80 to-cyan-900/70'></div>
      </div>

      {/* All scrollable content */}
      <div className='relative z-10'>
        {/* Navigation */}
        <nav className='fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='flex justify-between items-center h-20'>
            <div className='flex items-center'>
              <img 
                src='https://customer-assets.emergentagent.com/job_068c6455-6a9f-475f-b981-6fecca8bb2f5/artifacts/k0ki9pyo_ElvexTech%20Logo.jpg' 
                alt='Elvex Tech Logo' 
                className='h-16 w-auto'
              />
            </div>
            
            {/* Desktop Menu */}
            <div className='hidden md:flex items-center space-x-8'>
              <button onClick={() => scrollToSection('about')} className='nav-link'>About</button>
              <button onClick={() => scrollToSection('services')} className='nav-link'>Services</button>
              <button onClick={() => scrollToSection('process')} className='nav-link'>Process</button>
              <button onClick={() => scrollToSection('industries')} className='nav-link'>Industries</button>
              <button onClick={() => scrollToSection('why-us')} className='nav-link'>Why Us</button>
              <button onClick={() => scrollToSection('contact')} className='nav-link'>Contact</button>
              <Button 
                onClick={() => scrollToSection('contact')} 
                className='bg-cyan-500 hover:bg-cyan-600 text-white'
              >
                Get Quote
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className='md:hidden'
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className='w-6 h-6' /> : <Menu className='w-6 h-6' />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className='md:hidden bg-white border-t border-gray-200'>
            <div className='px-4 py-3 space-y-3'>
              <button onClick={() => scrollToSection('about')} className='mobile-nav-link'>About</button>
              <button onClick={() => scrollToSection('services')} className='mobile-nav-link'>Services</button>
              <button onClick={() => scrollToSection('process')} className='mobile-nav-link'>Process</button>
              <button onClick={() => scrollToSection('industries')} className='mobile-nav-link'>Industries</button>
              <button onClick={() => scrollToSection('why-us')} className='mobile-nav-link'>Why Us</button>
              <button onClick={() => scrollToSection('contact')} className='mobile-nav-link'>Contact</button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className='min-h-screen flex items-center justify-center text-white pt-20'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center'>
          <h1 className='text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6'>
            Elvex Tech Services
          </h1>
          <p className='text-lg sm:text-xl md:text-2xl mb-8 max-w-4xl mx-auto'>
            Premium B2B manufacturing bridge connecting global product innovators with India's most reliable and certified manufacturers
          </p>
          <Button 
            onClick={() => scrollToSection('contact')} 
            size='lg'
            className='bg-cyan-500 hover:bg-cyan-600 text-white px-8 py-6 text-lg border-0 shadow-lg'
          >
            Request a Manufacturing Consultation
          </Button>

          {/* Stats */}
          <div className='grid grid-cols-2 md:grid-cols-4 gap-6 mt-16'>
            {stats.map(stat => (
              <AnimatedStat key={stat.id} stat={stat} />
            ))}
          </div>

          {/* Scroll Indicator */}
          <div className='absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce'>
            <div className='flex flex-col items-center gap-2'>
              <svg 
                className='w-6 h-10 text-white opacity-80' 
                fill='none' 
                stroke='currentColor' 
                viewBox='0 0 24 24'
              >
                <path 
                  strokeLinecap='round' 
                  strokeLinejoin='round' 
                  strokeWidth={2} 
                  d='M19 14l-7 7m0 0l-7-7m7 7V3'
                />
              </svg>
            </div>
          </div>
        </div>
      </section>

      {/* Content sections with white background - scrolls over fixed hero */}
      <div className='bg-white relative z-20'>
        {/* About Section */}
        <section id='about' className='py-20'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8'>
          <AnimatedSection>
            <h2 className='text-3xl md:text-4xl font-bold text-center mb-4 text-navy'>About Elvex Tech</h2>
          </AnimatedSection>
          <div className='grid md:grid-cols-2 gap-12 items-center mt-12'>
            <AnimatedSection delay={100}>
              <div>
                <p className='text-lg text-gray-700 leading-relaxed mb-6'>
                  Elvex Tech Services is a global sourcing, manufacturing coordination, and product development support company connecting brands across the Middle East, the United States, and Europe with India's rapidly growing manufacturing ecosystem.
                </p>
                <p className='text-lg text-gray-700 leading-relaxed'>
                  We act as the bridge between innovation and execution—covering electronics, mechanical parts, plastics, packaging, cable assemblies, and complete product manufacturing.
                </p>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <div className='grid grid-cols-2 gap-4'>
                <img 
                  src='https://customer-assets.emergentagent.com/job_068c6455-6a9f-475f-b981-6fecca8bb2f5/artifacts/0wtutgpt_EMS1.jpg' 
                  alt='EMS Electronics Manufacturing' 
                  className='rounded-lg w-full h-48 object-cover hover:scale-105 transition-transform duration-300'
                />
                <img 
                  src='https://customer-assets.emergentagent.com/job_068c6455-6a9f-475f-b981-6fecca8bb2f5/artifacts/h56cf7rx_EMS2.jpg' 
                  alt='Electronic Manufacturing Services' 
                  className='rounded-lg w-full h-48 object-cover hover:scale-105 transition-transform duration-300 mt-8'
                />
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className='py-20 bg-gray-50'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8'>
          <AnimatedSection>
            <h2 className='text-3xl md:text-4xl font-bold text-center mb-12 text-navy'>Vision & Mission</h2>
          </AnimatedSection>
          <div className='grid md:grid-cols-2 gap-8'>
            <AnimatedSection delay={100}>
              <Card className='border-t-4 border-t-burgundy h-full'>
                <CardHeader>
                  <CardTitle className='text-2xl text-navy'>Vision</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className='text-gray-700 leading-relaxed'>
                    To become the world's most trusted bridge between global product innovators and India's manufacturing excellence.
                  </p>
                </CardContent>
              </Card>
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <Card className='border-t-4 border-t-burgundy h-full'>
                <CardHeader>
                  <CardTitle className='text-2xl text-navy'>Mission</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className='space-y-3 text-gray-700'>
                    <li className='flex items-start'>
                      <span className='text-burgundy mr-2'>•</span>
                      Enable reliable manufacturing partnerships in India
                    </li>
                    <li className='flex items-start'>
                      <span className='text-burgundy mr-2'>•</span>
                      Simplify sourcing, prototyping, and mass production
                    </li>
                    <li className='flex items-start'>
                      <span className='text-burgundy mr-2'>•</span>
                      Deliver transparent pricing and uncompromised quality
                    </li>
                    <li className='flex items-start'>
                      <span className='text-burgundy mr-2'>•</span>
                      Support startups and SMEs with end-to-end execution
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id='services' className='py-20 bg-white'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <AnimatedSection>
            <div className='text-center mb-12'>
              <h2 className='text-3xl md:text-4xl font-bold mb-4 text-navy'>Our Services</h2>
              <p className='text-gray-600'>End-to-end manufacturing solutions to bring your products to market</p>
            </div>
          </AnimatedSection>
          <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-8'>
            {services.map((service, index) => (
              <AnimatedSection key={service.id} delay={index * 100}>
                <Card className='hover:shadow-xl transition-shadow border-t-4 border-t-burgundy h-full'>
                  <CardHeader>
                    <div className='w-12 h-12 bg-navy rounded-lg flex items-center justify-center text-white mb-4'>
                      {getIcon(service.icon)}
                    </div>
                    <CardTitle className='text-xl text-navy'>{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className='text-gray-600 leading-relaxed'>
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Manufacturing Process */}
      <section id='process' className='py-20 bg-gray-50'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <AnimatedSection>
            <div className='text-center mb-12'>
              <h2 className='text-3xl md:text-4xl font-bold mb-4 text-navy'>Our Manufacturing Process</h2>
              <p className='text-gray-600'>A streamlined approach from concept to delivery</p>
            </div>
          </AnimatedSection>
          <div className='space-y-12'>
            {manufacturingProcess.map((item, index) => (
              <AnimatedSection key={item.id} delay={index * 100}>
                <div className={`grid md:grid-cols-2 gap-8 items-center ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}>
                  <div className={index % 2 === 1 ? 'md:order-2' : ''}>
                    <img 
                      src={item.image} 
                      alt={item.title}
                      className='rounded-lg w-full h-64 object-cover shadow-lg'
                    />
                  </div>
                  <div className={index % 2 === 1 ? 'md:order-1' : ''}>
                    <div className='inline-block px-4 py-2 bg-burgundy text-white rounded-full text-sm font-semibold mb-4'>
                      {item.step}
                    </div>
                    <h3 className='text-2xl font-bold mb-4 text-navy'>{item.title}</h3>
                    <p className='text-gray-700 leading-relaxed'>{item.description}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Industries We Serve */}
      <section id='industries' className='py-20 bg-white'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <AnimatedSection>
            <div className='text-center mb-12'>
              <h2 className='text-3xl md:text-4xl font-bold mb-4 text-navy'>Industries We Serve</h2>
              <p className='text-gray-600'>Trusted by businesses across diverse sectors</p>
            </div>
          </AnimatedSection>
          <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-8'>
            {industries.map((industry, index) => (
              <AnimatedSection key={industry.id} delay={index * 100}>
                <Card className='overflow-hidden hover:shadow-xl transition-shadow group h-full'>
                  <div className='relative h-48 overflow-hidden'>
                    <img 
                      src={industry.image} 
                      alt={industry.name}
                      className='w-full h-full object-cover group-hover:scale-110 transition-transform duration-300'
                    />
                  </div>
                  <CardHeader>
                    <div className='w-10 h-10 bg-navy rounded-lg flex items-center justify-center text-white mb-2'>
                      {getIcon(industry.icon)}
                    </div>
                    <CardTitle className='text-xl text-navy'>{industry.name}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className='text-gray-600'>{industry.description}</p>
                  </CardContent>
                </Card>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section id='why-us' className='py-20 bg-gray-50'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <AnimatedSection>
            <div className='text-center mb-12'>
              <h2 className='text-3xl md:text-4xl font-bold mb-4 text-navy'>Why Choose Elvex Tech</h2>
              <p className='text-gray-600'>Your trusted partner for manufacturing excellence</p>
            </div>
          </AnimatedSection>
          <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-8'>
            {whyChooseUs.map((item, index) => (
              <AnimatedSection key={item.id} delay={index * 100}>
                <div className='bg-white p-6 rounded-lg hover:shadow-lg transition-shadow h-full'>
                  <div className='w-12 h-12 bg-burgundy rounded-lg flex items-center justify-center text-white mb-4'>
                    {getIcon(item.icon)}
                  </div>
                  <h3 className='text-xl font-semibold mb-3 text-navy'>{item.title}</h3>
                  <p className='text-gray-600'>{item.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id='contact' className='py-20 bg-white'>
        <div className='max-w-6xl mx-auto px-4 sm:px-6 lg:px-8'>
          <AnimatedSection>
            <div className='text-center mb-12'>
              <h2 className='text-3xl md:text-4xl font-bold mb-4 text-navy'>Contact Us</h2>
              <p className='text-gray-600'>Ready to start your manufacturing journey?</p>
            </div>
          </AnimatedSection>
          <div className='grid md:grid-cols-2 gap-12'>
            <AnimatedSection delay={100}>
              <div>
                <h3 className='text-2xl font-semibold mb-6 text-navy'>Get in Touch</h3>
                <div className='space-y-6'>
                  <div className='flex items-start space-x-4'>
                    <MapPin className='w-6 h-6 text-burgundy mt-1 flex-shrink-0' />
                    <div>
                      <p className='font-semibold text-navy'>Address</p>
                      <p className='text-gray-600'>765, 2nd Main C-Block AECS Layout<br />Kundalahalli, Bangalore 560057</p>
                    </div>
                  </div>
                  <div className='flex items-start space-x-4'>
                    <Mail className='w-6 h-6 text-burgundy mt-1' />
                    <div>
                      <p className='font-semibold text-navy'>Email</p>
                      <a href='mailto:info@elvextech.com' className='text-gray-600 hover:text-burgundy transition-colors'>info@elvextech.com</a>
                    </div>
                  </div>
                  <div className='flex items-start space-x-4'>
                    <MessageSquare className='w-6 h-6 text-burgundy mt-1' />
                    <div>
                      <p className='font-semibold text-navy'>Website</p>
                      <a href='https://www.elvextech.com' target='_blank' rel='noopener noreferrer' className='text-gray-600 hover:text-burgundy transition-colors'>www.elvextech.com</a>
                    </div>
                  </div>
                </div>
                <div className='mt-8 p-6 bg-gray-50 rounded-lg'>
                  <p className='text-sm text-gray-600'>ISO-aligned | IPC-driven | Export-compliant</p>
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <div>
                <Card>
                  <CardHeader>
                    <CardTitle className='text-navy'>Send us a Message</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <form 
                      name="contact" 
                      method="POST"
                      action="/"
                      data-netlify="true"
                      netlify-honeypot="bot-field"
                      className='space-y-4'
                    >
                      <input type="hidden" name="form-name" value="contact" />
                      <p hidden>
                        <input name="bot-field" />
                      </p>
                      <div>
                        <Input 
                          type='text'
                          name='name'
                          placeholder='Name'
                          required
                        />
                      </div>
                      <div>
                        <Input 
                          type='email'
                          name='email'
                          placeholder='Email'
                          required
                        />
                      </div>
                      <div>
                        <Input 
                          type='text'
                          name='company'
                          placeholder='Company'
                        />
                      </div>
                      <div>
                        <Textarea 
                          name='message'
                          placeholder='Message'
                          required
                          rows={4}
                        />
                      </div>
                      <Button type='submit' className='w-full bg-cyan-500 hover:bg-cyan-600'>
                        Send Message
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className='bg-navy text-white py-12'>
        <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
          <div className='grid md:grid-cols-3 gap-8 mb-8'>
            <div>
              <h4 className='font-semibold mb-4'>Services</h4>
              <ul className='space-y-2 text-sm opacity-80'>
                <li>Vendor Sourcing</li>
                <li>Project Management</li>
                <li>Quality Control</li>
                <li>Logistics Support</li>
              </ul>
            </div>
            <div>
              <h4 className='font-semibold mb-4'>Industries</h4>
              <ul className='space-y-2 text-sm opacity-80'>
                <li>Electronics & EMS</li>
                <li>Industrial Products</li>
                <li>Plastics & Molding</li>
                <li>Consumer Goods</li>
              </ul>
            </div>
            <div>
              <h4 className='font-semibold mb-4'>Connect</h4>
              <ul className='space-y-2 text-sm opacity-80'>
                <li>info@elvextech.com</li>
                <li>www.elvextech.com</li>
              </ul>
            </div>
          </div>
          <div className='border-t border-white/20 pt-8 text-center'>
            <p className='text-sm opacity-80 mb-2'>© {new Date().getFullYear()} Elvex Tech Services. All rights reserved.</p>
            <p className='text-sm opacity-80'>
              <button 
                onClick={() => setPrivacyModalOpen(true)} 
                className='hover:text-cyan-400 transition-colors underline cursor-pointer bg-transparent border-0'
              >
                Privacy Policy
              </button>
              {' '} | {' '}
              <button 
                onClick={() => setCookieModalOpen(true)}
                className='hover:text-cyan-400 transition-colors underline cursor-pointer bg-transparent border-0'
              >
                Cookie Policy
              </button>
              {' '} | {' '}
              <button 
                onClick={() => setTermsModalOpen(true)}
                className='hover:text-cyan-400 transition-colors underline cursor-pointer bg-transparent border-0'
              >
                Terms of Service
              </button>
            </p>
          </div>
        </div>
      </footer>

      </div> {/* End of white content wrapper */}
      </div> {/* End of scrollable content wrapper */}

      {/* WhatsApp Floating Button */}
      <a
        href='https://wa.me/919567180100'
        target='_blank'
        rel='noopener noreferrer'
        className='fixed bottom-6 right-6 w-14 h-14 bg-green-500 rounded-full flex items-center justify-center shadow-lg hover:bg-green-600 transition-colors z-50'
        aria-label='Chat on WhatsApp'
      >
        <MessageSquare className='w-6 h-6 text-white' />
      </a>

      {/* Cookie Consent Banner */}
      <CookieConsent 
        onPrivacyClick={() => setPrivacyModalOpen(true)}
        onCookiePolicyClick={() => setCookieModalOpen(true)}
      />

      {/* Policy Modals */}
      <Modal 
        isOpen={privacyModalOpen} 
        onClose={() => setPrivacyModalOpen(false)} 
        title="Privacy Policy"
      >
        <PrivacyPolicyContent />
      </Modal>

      <Modal 
        isOpen={cookieModalOpen} 
        onClose={() => setCookieModalOpen(false)} 
        title="Cookie Policy"
      >
        <CookiePolicyContent />
      </Modal>

      <Modal 
        isOpen={termsModalOpen} 
        onClose={() => setTermsModalOpen(false)} 
        title="Terms of Service"
      >
        <TermsOfServiceContent />
      </Modal>
    </div>
  );
};

export default Home;